import { Fragment, useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import {
  UploadCloud, FileText, Trash2, XCircle, Loader2, Send, Image as ImageIcon,
  RefreshCw, PackageOpen, Paperclip, ChevronDown, ChevronUp, Plus,
} from "lucide-react";
import { PageHero } from "../../components/shared/Primitives";
import { api, API, formatApiError } from "../../lib/api";
import QuestionnaireBuilder from "../../components/admin/shop/QuestionnaireBuilder";
import ServiceOrderPanel from "../../components/admin/shop/ServiceOrderPanel";

/**
 * JCC Shop admin — self-serve product + order management for both
 * digital downloads and personalised services (see /app/memory/PRD.md).
 * A sidebar lists every product (any status); the right panel edits
 * whichever one is selected. Orders table below covers both types;
 * service orders expand into a dedicated workflow panel.
 */
const CURRENCY_OPTIONS = ["gbp", "usd", "eur"];

function toLines(arr) {
  return (arr || []).join("\n");
}
function fromLines(text) {
  return text.split("\n").map((l) => l.trim()).filter(Boolean);
}

function blankForm() {
  return {
    title: "", subtitle: "", price: "5.00", currency: "gbp", format_label: "",
    descriptionText: "", card_topicsText: "", card_structure_note: "",
    product_info_bulletsText: "", usage_terms: "", participation_note: "",
    product_type: "digital_download", member_price: "", turnaround_time: "",
    return_instructions: "", applies_to_carer_profile: false, questionnaire: [],
  };
}

function productToForm(p) {
  return {
    title: p.title || "", subtitle: p.subtitle || "",
    price: ((p.price_pence || 0) / 100).toFixed(2), currency: p.currency || "gbp",
    format_label: p.format_label || "",
    descriptionText: toLines(p.description), card_topicsText: toLines(p.card_topics),
    card_structure_note: p.card_structure_note || "",
    product_info_bulletsText: toLines(p.product_info_bullets),
    usage_terms: p.usage_terms || "", participation_note: p.participation_note || "",
    product_type: p.product_type || "digital_download",
    member_price: p.member_price_pence != null ? (p.member_price_pence / 100).toFixed(2) : "",
    turnaround_time: p.turnaround_time || "", return_instructions: p.return_instructions || "",
    applies_to_carer_profile: !!p.applies_to_carer_profile, questionnaire: p.questionnaire || [],
  };
}

function statusPill(status) {
  const styles = {
    draft: "bg-slate-100 text-ink-muted", published: "bg-teal/10 text-teal", withdrawn: "bg-amber-100 text-amber-700",
  };
  return <span className={`pill ${styles[status] || "bg-slate-100"}`}>{status}</span>;
}

export default function AdminShop() {
  const [products, setProducts] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [product, setProduct] = useState(null);
  const [form, setForm] = useState(blankForm());
  const [creating, setCreating] = useState(false);
  const [newSlug, setNewSlug] = useState("");
  const [newType, setNewType] = useState("digital_download");
  const [saving, setSaving] = useState(false);
  const [pdfUploading, setPdfUploading] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [attachmentUploading, setAttachmentUploading] = useState(false);
  const [statusSaving, setStatusSaving] = useState(false);
  const [orders, setOrders] = useState(null);
  const [showPending, setShowPending] = useState(false);
  const [resendingId, setResendingId] = useState(null);
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const pdfInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const attachmentInputRef = useRef(null);
  const requestIdRef = useRef(0);

  const loadProducts = useCallback(async () => {
    try {
      const { data } = await api.get("/api/admin/shop/products");
      setProducts(data.products || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not load products.");
      setProducts([]);
    }
  }, []);

  const loadProductDetail = useCallback(async (id) => {
    const myRequestId = ++requestIdRef.current;
    setSelectedId(id);
    const { data } = await api.get(`/api/admin/shop/products/${id}`);
    if (myRequestId !== requestIdRef.current) return;
    setProduct(data);
    setForm(productToForm(data));
  }, []);

  const loadOrders = useCallback(async () => {
    try {
      const { data } = await api.get("/api/admin/shop/orders");
      setOrders(data.orders || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not load orders.");
      setOrders([]);
    }
  }, []);

  useEffect(() => {
    loadProducts();
    loadOrders();
  }, [loadProducts, loadOrders]);

  useEffect(() => {
    if (products && products.length > 0 && !selectedId) {
      loadProductDetail(products[0].id);
    }
  }, [products, selectedId, loadProductDetail]);

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const isService = form.product_type === "service";

  const pendingCount = (orders || []).filter((o) => o.status === "pending").length;
  const visibleOrders = showPending ? (orders || []) : (orders || []).filter((o) => o.status !== "pending");
  const productById = Object.fromEntries((products || []).map((p) => [p.id, p]));

  const onCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/api/admin/shop/products", {
        slug: newSlug, title: "New product", price_pence: 500, currency: "gbp", product_type: newType,
      });
      toast.success("Product created as a draft.");
      setCreating(false);
      setNewSlug("");
      await loadProducts();
      await loadProductDetail(data.id);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not create product.");
    } finally {
      setSaving(false);
    }
  };

  const onSave = async (e) => {
    e.preventDefault();
    if (!product) return;
    setSaving(true);
    try {
      const payload = {
        title: form.title, subtitle: form.subtitle,
        price_pence: Math.round(parseFloat(form.price || "0") * 100), currency: form.currency,
        format_label: form.format_label,
        description: fromLines(form.descriptionText), card_topics: fromLines(form.card_topicsText),
        card_structure_note: form.card_structure_note,
        product_info_bullets: fromLines(form.product_info_bulletsText),
        usage_terms: form.usage_terms, participation_note: form.participation_note,
        product_type: form.product_type,
        member_price_pence: form.member_price.trim() === "" ? null : Math.round(parseFloat(form.member_price) * 100),
        turnaround_time: form.turnaround_time, return_instructions: form.return_instructions,
        applies_to_carer_profile: form.applies_to_carer_profile, questionnaire: form.questionnaire,
      };
      const { data } = await api.put(`/api/admin/shop/products/${product.id}`, payload);
      setProduct((p) => ({ ...p, ...data, preview_images: p.preview_images, attachments: p.attachments }));
      await loadProducts();
      toast.success("Product saved.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not save product.");
    } finally {
      setSaving(false);
    }
  };

  const onStatusChange = async (status) => {
    if (!product) return;
    setStatusSaving(true);
    try {
      await api.post(`/api/admin/shop/products/${product.id}/status`, { status });
      setProduct((p) => ({ ...p, status }));
      setProducts((list) => list.map((p) => (p.id === product.id ? { ...p, status } : p)));
      toast.success(`Product is now ${status}.`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not update status.");
    } finally {
      setStatusSaving(false);
    }
  };

  const onPdfSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !product) return;
    setPdfUploading(true);
    try {
      const body = new FormData();
      body.append("file", file);
      const { data } = await api.post(`/api/admin/shop/products/${product.id}/pdf`, body, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setProduct((p) => ({ ...p, has_pdf: data.has_pdf, pdf_filename: data.pdf_filename }));
      toast.success("Customer PDF uploaded.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not upload PDF.");
    } finally {
      setPdfUploading(false);
    }
  };

  const onImageSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !product) return;
    setImageUploading(true);
    try {
      const body = new FormData();
      body.append("file", file);
      await api.post(`/api/admin/shop/products/${product.id}/preview-image`, body, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await loadProductDetail(product.id);
      toast.success("Preview image added.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not upload image.");
    } finally {
      setImageUploading(false);
    }
  };

  const onRemoveImage = async (index) => {
    if (!product) return;
    try {
      await api.delete(`/api/admin/shop/products/${product.id}/preview-image/${index}`);
      await loadProductDetail(product.id);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not remove image.");
    }
  };

  const onAttachmentSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !product) return;
    setAttachmentUploading(true);
    try {
      const body = new FormData();
      body.append("file", file);
      await api.post(`/api/admin/shop/products/${product.id}/attachment`, body, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await loadProductDetail(product.id);
      toast.success("Attachment added.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not upload attachment.");
    } finally {
      setAttachmentUploading(false);
    }
  };

  const onRemoveAttachment = async (index) => {
    if (!product) return;
    try {
      await api.delete(`/api/admin/shop/products/${product.id}/attachment/${index}`);
      await loadProductDetail(product.id);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not remove attachment.");
    }
  };

  const onResendEmail = async (orderId) => {
    setResendingId(orderId);
    try {
      const { data } = await api.post(`/api/admin/shop/orders/${orderId}/resend-email`);
      toast.success(`Delivery email ${data.email_status}.`);
      loadOrders();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not resend email.");
    } finally {
      setResendingId(null);
    }
  };

  const onOrderUpdated = (orderId, patch) => {
    setOrders((list) => list.map((o) => (o.id === orderId ? { ...o, ...patch } : o)));
  };

  return (
    <div data-testid="admin-shop-page">
      <PageHero eyebrow="JCC Admin" title="JCC Shop"
        description="Manage digital downloads and personalised services, their pricing, files, questionnaires and orders." />
      <section className="section pt-8">
        <div className="container-jcc grid lg:grid-cols-4 gap-8">
          <div className="lg:col-span-1 space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="font-display font-semibold">Products</h2>
              <button onClick={() => setCreating((v) => !v)} className="text-teal text-sm flex items-center gap-1" data-testid="admin-shop-toggle-create">
                <Plus className="w-4 h-4" /> Add
              </button>
            </div>
            {creating && (
              <form onSubmit={onCreate} className="card-base p-4 space-y-3" data-testid="admin-shop-create-form">
                <input required value={newSlug} onChange={(e) => setNewSlug(e.target.value)}
                  placeholder="url-slug-for-product" data-testid="admin-shop-new-slug"
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" />
                <select value={newType} onChange={(e) => setNewType(e.target.value)} data-testid="admin-shop-new-type"
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm">
                  <option value="digital_download">Digital download</option>
                  <option value="service">Personalised service</option>
                </select>
                <button disabled={saving} className="btn-teal text-sm w-full justify-center" data-testid="admin-shop-create-submit">
                  {saving ? "Creating…" : "Create draft"}
                </button>
              </form>
            )}
            {products === null && <p className="text-ink-muted text-sm" data-testid="admin-shop-loading">Loading…</p>}
            {products && products.length === 0 && !creating && (
              <p className="text-ink-muted text-sm" data-testid="admin-shop-no-products">No products yet — click Add to create one.</p>
            )}
            <div className="space-y-1.5">
              {(products || []).map((p) => (
                <button key={p.id} onClick={() => loadProductDetail(p.id)} data-testid={`admin-shop-product-item-${p.id}`}
                  className={`w-full text-left rounded-xl border px-3 py-2.5 text-sm ${selectedId === p.id ? "border-teal bg-teal/5" : "border-slate-200 hover:border-slate-300"}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium truncate">{p.title}</span>
                    {statusPill(p.status)}
                  </div>
                  <span className="text-xs text-ink-muted">{p.product_type === "service" ? "Service" : "Download"} · {p.price_display}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="lg:col-span-3 space-y-8">
            {product && (
              <>
                <div className="card-base p-6" data-testid="admin-shop-status-panel">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="text-sm text-ink-muted">Current status</p>
                      <p className="font-display font-bold text-xl capitalize" data-testid="admin-shop-current-status">
                        {product.status}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button onClick={() => onStatusChange("draft")} disabled={statusSaving || product.status === "draft"}
                        className="btn-outline text-sm disabled:opacity-50" data-testid="admin-shop-set-draft">Save as draft</button>
                      <button onClick={() => onStatusChange("published")} disabled={statusSaving || product.status === "published"}
                        className="btn-teal text-sm disabled:opacity-50" data-testid="admin-shop-set-published">Publish</button>
                      <button onClick={() => onStatusChange("withdrawn")} disabled={statusSaving || product.status === "withdrawn"}
                        className="btn-outline text-sm disabled:opacity-50" data-testid="admin-shop-set-withdrawn">Withdraw from sale</button>
                    </div>
                  </div>
                  {product.product_type !== "service" && !product.has_pdf && (
                    <p className="mt-3 text-sm text-amber-700 flex items-center gap-2" data-testid="admin-shop-no-pdf-warning">
                      <XCircle className="w-4 h-4 shrink-0" /> Upload the final customer PDF below before publishing —
                      publishing is blocked until it's uploaded.
                    </p>
                  )}
                  {product.status === "withdrawn" && (
                    <p className="mt-3 text-sm text-ink-muted">
                      Withdrawn from sale — existing buyers keep access; new purchases are blocked.
                    </p>
                  )}
                </div>

                {product.product_type !== "service" && (
                  <div className="card-base p-6 space-y-4" data-testid="admin-shop-pdf-panel">
                    <h2 className="font-display font-semibold text-lg flex items-center gap-2">
                      <FileText className="w-5 h-5 text-purple" /> Customer PDF (private)
                    </h2>
                    <p className="text-sm text-ink-muted">
                      {product.has_pdf
                        ? `Current file: ${product.pdf_filename}`
                        : "No PDF uploaded yet. This file is never publicly accessible — it is only released via a paid order's secure download link."}
                    </p>
                    <input ref={pdfInputRef} type="file" accept="application/pdf" onChange={onPdfSelected} className="hidden" data-testid="admin-shop-pdf-input" />
                    <button type="button" onClick={() => pdfInputRef.current?.click()} disabled={pdfUploading}
                      className="btn-outline text-sm" data-testid="admin-shop-pdf-upload-btn">
                      {pdfUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />}
                      {product.has_pdf ? "Replace PDF" : "Upload PDF"} (max 10MB)
                    </button>
                  </div>
                )}

                <div className="card-base p-6 space-y-4" data-testid="admin-shop-images-panel">
                  <h2 className="font-display font-semibold text-lg flex items-center gap-2">
                    <ImageIcon className="w-5 h-5 text-teal" /> Public sample previews
                  </h2>
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                    {(product.preview_images || []).map((src, i) => (
                      <div key={i} className="relative group" data-testid={`admin-shop-preview-${i}`}>
                        <img src={src} alt={`Preview ${i + 1}`} className="rounded-xl border border-slate-200 w-full object-cover aspect-[3/4]" />
                        <button type="button" onClick={() => onRemoveImage(i)}
                          className="absolute top-1.5 right-1.5 bg-white rounded-full p-1.5 shadow border border-slate-200"
                          data-testid={`admin-shop-remove-preview-${i}`}>
                          <Trash2 className="w-3.5 h-3.5 text-red-600" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <input ref={imageInputRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={onImageSelected} className="hidden" data-testid="admin-shop-image-input" />
                  <button type="button" onClick={() => imageInputRef.current?.click()} disabled={imageUploading || (product.preview_images || []).length >= 6}
                    className="btn-outline text-sm" data-testid="admin-shop-image-upload-btn">
                    {imageUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />}
                    Add preview image
                  </button>
                </div>

                <div className="card-base p-6 space-y-4" data-testid="admin-shop-attachments-panel">
                  <h2 className="font-display font-semibold text-lg flex items-center gap-2">
                    <Paperclip className="w-5 h-5 text-purple" /> Extra attachments
                  </h2>
                  <p className="text-sm text-ink-muted">
                    Extra files (guides, forms, reference documents) delivered alongside this order — up to 5, 10MB each.
                  </p>
                  <div className="space-y-2">
                    {(product.attachments || []).map((a, i) => (
                      <div key={i} className="flex items-center justify-between rounded-xl border border-slate-200 px-3 py-2 text-sm" data-testid={`admin-shop-attachment-${i}`}>
                        <span className="truncate">{a.filename}</span>
                        <button type="button" onClick={() => onRemoveAttachment(i)} data-testid={`admin-shop-remove-attachment-${i}`}>
                          <Trash2 className="w-3.5 h-3.5 text-red-600" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <input ref={attachmentInputRef} type="file" onChange={onAttachmentSelected} className="hidden" data-testid="admin-shop-attachment-input" />
                  <button type="button" onClick={() => attachmentInputRef.current?.click()} disabled={attachmentUploading || (product.attachments || []).length >= 5}
                    className="btn-outline text-sm" data-testid="admin-shop-attachment-upload-btn">
                    {attachmentUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />}
                    Add attachment
                  </button>
                </div>

                <form onSubmit={onSave} className="card-base p-6 space-y-4" data-testid="admin-shop-product-form">
                  <h2 className="font-display font-semibold text-lg">Product details</h2>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1.5">Product type</label>
                      <select value={form.product_type} onChange={setField("product_type")} data-testid="admin-shop-product-type"
                        className="w-full rounded-xl border border-slate-200 px-4 py-2.5">
                        <option value="digital_download">Digital download</option>
                        <option value="service">Personalised service</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1.5">Title</label>
                      <input value={form.title} onChange={setField("title")} data-testid="admin-shop-title"
                        className="w-full rounded-xl border border-slate-200 px-4 py-2.5" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1.5">Subtitle</label>
                      <input value={form.subtitle} onChange={setField("subtitle")} data-testid="admin-shop-subtitle"
                        className="w-full rounded-xl border border-slate-200 px-4 py-2.5" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1.5">Price</label>
                      <div className="flex gap-2">
                        <input value={form.price} onChange={setField("price")} data-testid="admin-shop-price"
                          className="w-full rounded-xl border border-slate-200 px-4 py-2.5" />
                        <select value={form.currency} onChange={setField("currency")} data-testid="admin-shop-currency"
                          className="rounded-xl border border-slate-200 px-3 py-2.5">
                          {CURRENCY_OPTIONS.map((c) => <option key={c} value={c}>{c.toUpperCase()}</option>)}
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1.5">Format label</label>
                      <input value={form.format_label} onChange={setField("format_label")} data-testid="admin-shop-format-label"
                        className="w-full rounded-xl border border-slate-200 px-4 py-2.5" />
                    </div>
                    {isService && (
                      <div>
                        <label className="block text-sm font-medium mb-1.5">Pro Carer Office member price (leave blank for no discount, 0 = free)</label>
                        <input value={form.member_price} onChange={setField("member_price")} data-testid="admin-shop-member-price"
                          placeholder="e.g. 0.00" className="w-full rounded-xl border border-slate-200 px-4 py-2.5" />
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1.5">Description (one paragraph per line)</label>
                    <textarea rows={4} value={form.descriptionText} onChange={setField("descriptionText")} data-testid="admin-shop-description"
                      className="w-full rounded-xl border border-slate-200 px-4 py-2.5" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1.5">Card topics (one per line)</label>
                    <textarea rows={3} value={form.card_topicsText} onChange={setField("card_topicsText")} data-testid="admin-shop-card-topics"
                      className="w-full rounded-xl border border-slate-200 px-4 py-2.5" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1.5">Card structure note</label>
                    <input value={form.card_structure_note} onChange={setField("card_structure_note")} data-testid="admin-shop-card-structure-note"
                      className="w-full rounded-xl border border-slate-200 px-4 py-2.5" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1.5">Product information bullets (one per line)</label>
                    <textarea rows={3} value={form.product_info_bulletsText} onChange={setField("product_info_bulletsText")} data-testid="admin-shop-info-bullets"
                      className="w-full rounded-xl border border-slate-200 px-4 py-2.5" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1.5">Usage terms</label>
                    <textarea rows={3} value={form.usage_terms} onChange={setField("usage_terms")} data-testid="admin-shop-usage-terms"
                      className="w-full rounded-xl border border-slate-200 px-4 py-2.5" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1.5">Participation note</label>
                    <textarea rows={2} value={form.participation_note} onChange={setField("participation_note")} data-testid="admin-shop-participation-note"
                      className="w-full rounded-xl border border-slate-200 px-4 py-2.5" />
                  </div>

                  {isService && (
                    <>
                      <div className="grid sm:grid-cols-2 gap-4 pt-2 border-t border-slate-200">
                        <div>
                          <label className="block text-sm font-medium mb-1.5">Turnaround time</label>
                          <input value={form.turnaround_time} onChange={setField("turnaround_time")} data-testid="admin-shop-turnaround-time"
                            placeholder="e.g. 5-7 business days" className="w-full rounded-xl border border-slate-200 px-4 py-2.5" />
                        </div>
                        <div className="flex items-end">
                          <label className="flex items-center gap-2 text-sm">
                            <input type="checkbox" checked={form.applies_to_carer_profile}
                              onChange={(e) => setForm((f) => ({ ...f, applies_to_carer_profile: e.target.checked }))}
                              data-testid="admin-shop-applies-to-profile" />
                            Approved wording can be added to the customer's Carer Profile
                          </label>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1.5">Return instructions (shown to the customer after they pay)</label>
                        <textarea rows={3} value={form.return_instructions} onChange={setField("return_instructions")} data-testid="admin-shop-return-instructions"
                          className="w-full rounded-xl border border-slate-200 px-4 py-2.5" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1.5">Questionnaire (customer completes this after checkout, once payment is confirmed)</label>
                        <QuestionnaireBuilder questions={form.questionnaire} onChange={(q) => setForm((f) => ({ ...f, questionnaire: q }))} />
                      </div>
                    </>
                  )}

                  <button disabled={saving} className="btn-teal" data-testid="admin-shop-save-btn">
                    {saving ? "Saving…" : "Save product"}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>

        <div className="container-jcc max-w-5xl mt-10">
          <div className="card-base p-6" data-testid="admin-shop-orders-panel">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display font-semibold text-lg">Orders</h2>
              <div className="flex items-center gap-4">
                <label className="text-xs text-ink-muted flex items-center gap-1.5">
                  <input type="checkbox" checked={showPending} onChange={(e) => setShowPending(e.target.checked)}
                    data-testid="admin-shop-show-pending-toggle" />
                  Show pending / unpaid attempts ({pendingCount})
                </label>
                <button onClick={loadOrders} className="text-sm text-teal flex items-center gap-1.5" data-testid="admin-shop-refresh-orders">
                  <RefreshCw className="w-4 h-4" /> Refresh
                </button>
              </div>
            </div>
            {orders === null && <p className="text-ink-muted text-sm">Loading…</p>}
            {orders && orders.length === 0 && <p className="text-ink-muted text-sm" data-testid="admin-shop-no-orders">No orders yet.</p>}
            {orders && orders.length > 0 && visibleOrders.length === 0 && (
              <p className="text-ink-muted text-sm" data-testid="admin-shop-no-visible-orders">
                No paid orders yet — {pendingCount} pending/unpaid attempt{pendingCount === 1 ? "" : "s"} hidden above.
              </p>
            )}
            {visibleOrders.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="admin-shop-orders-table">
                  <thead>
                    <tr className="text-left text-ink-soft border-b border-slate-200">
                      <th className="py-2 pr-3">Date</th>
                      <th className="py-2 pr-3">Product</th>
                      <th className="py-2 pr-3">Type</th>
                      <th className="py-2 pr-3">Email</th>
                      <th className="py-2 pr-3">Price</th>
                      <th className="py-2 pr-3">Status</th>
                      <th className="py-2 pr-3" />
                    </tr>
                  </thead>
                  <tbody>
                    {visibleOrders.map((o) => {
                      const prod = productById[o.product_id];
                      const expanded = expandedOrderId === o.id;
                      return (
                        <Fragment key={o.id}>
                          <tr className="border-b border-slate-100" data-testid={`admin-shop-order-${o.id}`}>
                            <td className="py-2 pr-3 whitespace-nowrap text-ink-soft">{new Date(o.created_at).toLocaleDateString()}</td>
                            <td className="py-2 pr-3">{o.product_title_snapshot}</td>
                            <td className="py-2 pr-3">
                              <span className={`pill ${o.is_service ? "bg-purple/10 text-purple" : "bg-teal/10 text-teal"}`}>
                                {o.is_service ? "Service" : "Download"}
                              </span>
                            </td>
                            <td className="py-2 pr-3">{o.customer_email || "—"}</td>
                            <td className="py-2 pr-3">{o.price_display}</td>
                            <td className="py-2 pr-3">
                              <span className={`pill ${o.status === "paid" ? "bg-teal/10 text-teal" : o.status === "expired" ? "bg-slate-100 text-ink-muted" : "bg-amber-100 text-amber-700"}`}>
                                {o.status}
                              </span>
                            </td>
                            <td className="py-2 pr-3">
                              {o.status === "paid" && !o.is_service && (
                                <button onClick={() => onResendEmail(o.id)} disabled={resendingId === o.id}
                                  className="text-teal font-medium flex items-center gap-1.5" data-testid={`admin-shop-resend-${o.id}`}>
                                  {resendingId === o.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                                  Resend
                                </button>
                              )}
                              {o.status === "paid" && o.is_service && (
                                <button onClick={() => setExpandedOrderId(expanded ? null : o.id)}
                                  className="text-teal font-medium flex items-center gap-1.5" data-testid={`admin-shop-manage-${o.id}`}>
                                  Manage {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                                </button>
                              )}
                            </td>
                          </tr>
                          {expanded && o.is_service && (
                            <tr>
                              <td colSpan={7}>
                                <ServiceOrderPanel
                                  order={{ ...o, applies_to_carer_profile: !!prod?.applies_to_carer_profile }}
                                  questionnaire={prod?.questionnaire}
                                  onUpdated={(patch) => onOrderUpdated(o.id, patch)}
                                />
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
